import { useState, useEffect, useCallback } from 'react';
import { formatDateTime } from '../../utils/dateFormatting';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Wrench, Play, Eye, RotateCcw, AlertTriangle, CheckCircle2, XCircle, Loader2, Clock, History
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const STATUS_BADGE = {
  running:  { cls: 'bg-blue-500/20 text-blue-300 border-blue-500',     icon: Loader2,        label: 'Running' },
  success:  { cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500', icon: CheckCircle2, label: 'Success' },
  failed:   { cls: 'bg-red-500/20 text-red-300 border-red-500',         icon: XCircle,        label: 'Failed' },
  never:    { cls: 'bg-slate-500/20 text-slate-300 border-slate-500',   icon: Clock,          label: 'Never run' },
};

const MODE_LABEL = { dry_run: 'Dry Run', execute: 'Execute', rollback: 'Rollback' };

const DANGER_BADGE = {
  low:    { cls: 'border-emerald-500 text-emerald-400', label: 'Low risk' },
  medium: { cls: 'border-amber-500 text-amber-400',     label: 'Medium risk' },
  high:   { cls: 'border-red-500 text-red-400',         label: 'High risk' },
};

const Migrations = () => {
  const { api, admin } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};

  const [loading, setLoading] = useState(true);
  const [migrations, setMigrations] = useState([]);

  const [confirm, setConfirm] = useState(null); // { migration, mode }
  const [running, setRunning] = useState(false);

  const [history, setHistory] = useState(null); // { migration, runs }
  const [historyLoading, setHistoryLoading] = useState(false);

  const [outputView, setOutputView] = useState(null); // run object

  const isAdmin = admin?.role === 'admin';

  // ==================== DATA ====================

  const fetchMigrations = useCallback(async () => {
    try {
      const res = await api.get('/api/super-admin/migrations');
      setMigrations(res.data?.migrations || []);
    } catch (err) {
      toast.error('Failed to load migrations', { description: err.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchMigrations(); }, [fetchMigrations]);

  // ==================== ACTIONS ====================

  const runNow = async () => {
    if (!confirm) return;
    setRunning(true);
    try {
      const res = await api.post(
        `/api/super-admin/migrations/${confirm.migration.key}/run`,
        { mode: confirm.mode }
      );
      const final = res.data;
      if (final.status === 'success') {
        toast.success(`${confirm.migration.title} — ${MODE_LABEL[confirm.mode]} succeeded`);
      } else {
        toast.error(`${MODE_LABEL[confirm.mode]} failed`, { description: final.error });
      }
      setConfirm(null);
      setOutputView(final);
      fetchMigrations();
    } catch (err) {
      toast.error('Run failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setRunning(false);
    }
  };

  const openHistory = async (migration) => {
    setHistory({ migration, runs: [] });
    setHistoryLoading(true);
    try {
      const res = await api.get(`/api/super-admin/migrations/${migration.key}/runs`);
      setHistory({ migration, runs: res.data?.runs || [] });
    } catch (err) {
      toast.error('Failed to load history', { description: err.response?.data?.detail || err.message });
    } finally {
      setHistoryLoading(false);
    }
  };

  // ==================== RENDER ====================

  const cardClasses = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm';
  const labelMuted = isDark ? 'text-slate-400' : 'text-gray-500';
  const textMain = isDark ? 'text-white' : 'text-gray-900';

  const renderStatusBadge = (lastRun) => {
    const key = lastRun?.status || 'never';
    const cfg = STATUS_BADGE[key] || STATUS_BADGE.never;
    const Icon = cfg.icon;
    return (
      <Badge variant="outline" className={`text-xs flex items-center gap-1 ${cfg.cls}`}>
        <Icon className={`h-3 w-3 ${key === 'running' ? 'animate-spin' : ''}`} /> {cfg.label}
      </Badge>
    );
  };

  if (!isAdmin) {
    return (
      <div className={`max-w-md mx-auto mt-12 text-center p-8 rounded-lg ${cardClasses}`}>
        <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
        <h2 className={`text-lg font-bold ${textMain}`}>Admin role required</h2>
        <p className={`text-sm mt-1 ${labelMuted}`}>Only super-admins (not support agents) can run migrations.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="migrations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold flex items-center gap-3 ${textMain}`}>
            <Wrench className="h-6 w-6 text-[#ffd108]" />
            Migrations
          </h1>
          <p className={`text-sm ${labelMuted}`}>
            Trigger and audit schema/data migrations. Always dry-run first; runs are audit-logged.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" />
        </div>
      ) : migrations.length === 0 ? (
        <Card className={cardClasses}>
          <CardContent className={`text-center py-12 ${labelMuted}`}>No migrations registered.</CardContent>
        </Card>
      ) : (
        migrations.map((m) => {
          const danger = DANGER_BADGE[m.danger] || DANGER_BADGE.low;
          const last = m.last_run;
          return (
            <Card key={m.key} className={cardClasses} data-testid={`migration-card-${m.key}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <CardTitle className={`flex items-center gap-2 ${textMain}`}>
                      {m.title}
                      <Badge variant="outline" className={`text-xs ${danger.cls}`}>{danger.label}</Badge>
                    </CardTitle>
                    <CardDescription className={`mt-1 ${labelMuted}`}>{m.description}</CardDescription>
                    <div className={`mt-2 text-xs font-mono ${labelMuted}`}>key: {m.key}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {renderStatusBadge(last)}
                    {last && (
                      <button
                        type="button"
                        onClick={() => setOutputView(last)}
                        className={`text-xs underline ${labelMuted} hover:text-[#ffd108]`}
                      >
                        last run · {MODE_LABEL[last.mode]} ·
                        {last.finished_at
                          ? ` ${formatDistanceToNow(new Date(last.finished_at), { addSuffix: true })}`
                          : ' running...'}
                        {last.run_by_name ? ` · ${last.run_by_name}` : ''}
                      </button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setConfirm({ migration: m, mode: 'dry_run' })}
                    data-testid={`migration-dryrun-${m.key}`}
                  >
                    <Eye className="mr-2 h-4 w-4" /> Dry Run
                  </Button>
                  <Button
                    onClick={() => setConfirm({ migration: m, mode: 'execute' })}
                    className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                    data-testid={`migration-execute-${m.key}`}
                  >
                    <Play className="mr-2 h-4 w-4" /> Execute
                  </Button>
                  {m.supports_rollback && (
                    <Button
                      variant="outline"
                      onClick={() => setConfirm({ migration: m, mode: 'rollback' })}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid={`migration-rollback-${m.key}`}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" /> Rollback
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => openHistory(m)}
                    data-testid={`migration-history-${m.key}`}
                  >
                    <History className="mr-2 h-4 w-4" /> History
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && !running && setConfirm(null)}>
        <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} max-w-md`}>
          <DialogHeader>
            <DialogTitle className={textMain}>
              {confirm ? `${MODE_LABEL[confirm.mode]}: ${confirm.migration.title}` : ''}
            </DialogTitle>
            <DialogDescription className={labelMuted}>
              {confirm?.mode === 'dry_run' && 'Reports what the migration would do. No data is changed. Safe.'}
              {confirm?.mode === 'execute' && 'Performs the migration. Idempotent; safely re-runnable. Run a dry-run first if unsure.'}
              {confirm?.mode === 'rollback' && 'Reverses the migration. Make sure no data has been written that depends on the new state.'}
            </DialogDescription>
          </DialogHeader>
          {confirm?.mode !== 'dry_run' && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300 flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>This action will be audit-logged and may take up to 5 minutes.</span>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)} disabled={running}>Cancel</Button>
            <Button
              onClick={runNow}
              disabled={running}
              className={confirm?.mode === 'rollback'
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-[#ffd108] text-black hover:bg-[#e5bc07]'}
              data-testid="migration-confirm-run-btn"
            >
              {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Output viewer */}
      <Dialog open={!!outputView} onOpenChange={(o) => !o && setOutputView(null)}>
        <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} max-w-2xl`}>
          <DialogHeader>
            <DialogTitle className={`${textMain} flex items-center gap-2`}>
              {outputView && renderStatusBadge(outputView)}
              {outputView?.mode ? MODE_LABEL[outputView.mode] : 'Run'} output
            </DialogTitle>
            <DialogDescription className={labelMuted}>
              {outputView?.run_by_name ? `Triggered by ${outputView.run_by_name} ` : ''}
              {outputView?.started_at ? `at ${formatDateTime(outputView.started_at)}` : ''}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-72 rounded border border-slate-700 bg-black/40 p-3 font-mono text-xs">
            {(outputView?.output || []).map((line, i) => (
              <div key={i} className={`${line.includes('FAIL') ? 'text-red-400'
                : line.includes('OK') ? 'text-emerald-400'
                : line.includes('SKIP') || line.includes('DRY') ? 'text-amber-300'
                : 'text-slate-200'} whitespace-pre`}>{line}</div>
            ))}
            {outputView?.error && (
              <div className="mt-2 text-red-400">ERROR: {outputView.error}</div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* History viewer */}
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} max-w-2xl`}>
          <DialogHeader>
            <DialogTitle className={textMain}>{history?.migration?.title} — Run history</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-80">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-[#ffd108]" />
              </div>
            ) : (history?.runs || []).length === 0 ? (
              <div className={`text-center py-8 ${labelMuted}`}>No runs yet.</div>
            ) : (
              <div className="space-y-2">
                {history.runs.map((run) => (
                  <button
                    key={run.id}
                    type="button"
                    onClick={() => setOutputView(run)}
                    className={`w-full text-left p-3 rounded border ${isDark ? 'border-slate-700 hover:bg-slate-700/40' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {renderStatusBadge(run)}
                        <span className={`text-xs ${labelMuted}`}>{MODE_LABEL[run.mode]}</span>
                      </div>
                      <span className={`text-xs ${labelMuted}`}>
                        {run.finished_at
                          ? formatDistanceToNow(new Date(run.finished_at), { addSuffix: true })
                          : 'running'}
                      </span>
                    </div>
                    <div className={`text-xs mt-1 ${labelMuted}`}>by {run.run_by_name || run.run_by}</div>
                    {run.error && <div className="text-xs mt-1 text-red-400">error: {run.error}</div>}
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Migrations;
